const FLOOR_HEIGHT = 3.2;

function arch(template, floor, index, name, generatorType, dimensions, position, material, extras = {}) {
  return {
    id: `${template.id}:house-architecture:${index}`,
    name,
    type: 'architecture',
    category: 'building',
    generatorType,
    dimensions,
    position,
    rotation: extras.rotation ?? [0, 0, 0],
    scale: [1, 1, 1],
    material,
    accentMaterial: extras.accentMaterial ?? material,
    floorId: floor?.id ?? null,
    roomId: floor?.rooms?.[0]?.id ?? null,
    visible: true,
    locked: extras.locked ?? false,
    structural: true,
    placementRules: { snapToFloor: true },
    ...extras
  };
}

export function getHouseFloorMassing(template, level) {
  const profile = template.houseProfile;
  const scale = level > 0 ? profile?.upperScale ?? [0.84, 0.82] : profile?.floorScale ?? [0.9, 0.88];
  const offset = level > 0 ? profile?.upperOffset ?? [0, 0] : [0, 0];
  return {
    width: Math.max(5.5, template.footprint.width * scale[0]),
    depth: Math.max(5.5, template.footprint.depth * scale[1]),
    centerX: offset[0] ?? 0,
    centerZ: offset[1] ?? 0,
    baseY: (profile?.baseElevation ?? 0) + level * FLOOR_HEIGHT
  };
}

export function createHouseArchitecture(template, floors) {
  const profile = template.houseProfile;
  if (!profile || !floors?.length) return [];
  const groundFloor = floors[0];
  const lower = getHouseFloorMassing(template, 0);
  const upper = floors[1] ? getHouseFloorMassing(template, 1) : null;
  const frontZ = lower.centerZ + lower.depth / 2;
  const side = profile.mirror ? -1 : 1;
  const accent = profile.accent;
  const items = [];
  const add = (name, type, dimensions, position, material = accent, extras = {}) => {
    const { floor = groundFloor, ...safeExtras } = extras;
    items.push(arch(template, floor, items.length, name, type, dimensions, position, material, safeExtras));
  };

  if (profile.stilts) {
    add('Raised timber foundation', 'stilt-foundation', [lower.width + 0.9, profile.baseElevation, lower.depth + 0.8], [lower.centerX, 0, lower.centerZ], accent);
  } else if (profile.plinth) {
    add('Architectural base plinth', 'base-plinth', [lower.width + 0.34, 0.2, lower.depth + 0.34], [lower.centerX, 0, lower.centerZ], 'stone-dark');
  }
  if (profile.veranda) {
    const verandaWidth = lower.width * (profile.id.startsWith('heritage') ? 0.78 : 0.56);
    add('Front veranda', 'veranda', [verandaWidth, 0.18, 1.55], [lower.centerX, lower.baseY - 0.12, frontZ + 0.68], accent, {
      postHeight: profile.id.startsWith('heritage') ? 2.5 : 2.25,
      roofMaterial: profile.roof
    });
  }
  if (profile.entrySteps || profile.baseElevation > 0.25) {
    add('Entrance steps', 'entry-steps', [1.55, Math.max(0.4, profile.baseElevation), 1.75], [lower.centerX, 0, frontZ + 1.72], accent);
  }
  if (profile.facadeFrame) {
    add('Framed front facade', 'facade-frame', [lower.width * 0.76, 2.75, 0.35], [lower.centerX, lower.baseY + 0.06, frontZ + 0.13], accent);
  }
  if (profile.carport) {
    const width = Math.max(2.8, lower.width * 0.28);
    const carportX = lower.centerX + side * (lower.width / 2 + width * 0.42);
    add('Covered carport', 'carport', [width, 2.55, Math.min(5.2, lower.depth * 0.56)], [lower.centerX + side * (lower.width / 2 + width * 0.42), 0, frontZ - 1.25], profile.roof, {
      accentMaterial: accent
    });
    if (profile.vehicle) {
      add('Procedural parked car', 'parked-car', [Math.min(1.85, width * 0.63), 1.18, 3.65], [carportX, 0.03, frontZ - 1.18], 'concrete-warm', {
        bodyMaterial: profile.variation % 2 ? 'wall-charcoal' : 'concrete-warm',
        locked: false
      });
    }
  }
  if (profile.balcony && upper) {
    add('Upper floor balcony', 'balcony', [Math.max(2.8, upper.width * 0.46), 0.18, 1.42], [upper.centerX - side * upper.width * 0.16, upper.baseY + 0.04, upper.centerZ + upper.depth / 2 + 0.63], accent, {
      floor: floors[1], railMaterial: 'metal-dark'
    });
  }
  if (profile.slatScreen && upper) {
    add('Vertical timber screen', 'slat-screen', [Math.max(1.35, upper.width * 0.2), 2.55, 0.2], [upper.centerX + side * upper.width * 0.31, upper.baseY + 0.18, upper.centerZ + upper.depth / 2 + 0.16], accent, {
      floor: floors[1], slatCount: 7 + profile.facadePattern
    });
  }

  if (!profile.id.startsWith('heritage')) {
    add('Facade accent volume', 'facade-panel', [Math.max(1.2, lower.width * 0.18), 2.55, 0.22], [lower.centerX - side * lower.width * 0.29, lower.baseY + 0.15, frontZ + 0.16], accent, {
      panelPattern: profile.facadePattern
    });
  }

  if (profile.planter || profile.landscapeDensity > 2) {
    const count = Math.min(4, 1 + profile.landscapeDensity);
    for (let index = 0; index < count; index += 1) {
      const x = lower.centerX + ((index / Math.max(1, count - 1)) - 0.5) * (lower.width + 2.2);
      add(`Facade planter ${index + 1}`, 'planter', [1.05 + (index % 2) * 0.35, 0.42, 0.58], [x, 0.02, frontZ + 2.1 + (index % 2) * 0.25], index % 2 ? 'stone-dark' : 'concrete-warm');
    }
  }
  if (profile.fence) {
    add('Low front boundary', 'fence', [template.footprint.width + 5.5, 0.95, 0.18], [0, 0, template.footprint.depth / 2 + 3.25], profile.wall, {
      accentMaterial: accent,
      gateWidth: 3.2
    });
  }
  if (profile.pergola) {
    add('Garden pergola', 'pergola', [3.2, 2.45, 2.3], [-side * (lower.width / 2 + 1.4), 0, lower.centerZ - lower.depth * 0.22], accent);
  }
  return items;
}
